import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const MARS_DETAILS: Record<string, {
  id: string; title: string; subtitle: string; color: string; icon: string;
  heroImg: string; overview: string;
  specs: { label: string; value: string }[];
  scenes: { title: string; desc: string; detail: string; icon: string; img: string }[];
  roadmap: { year: string; event: string; status: 'done' | 'active' | 'future' }[];
  gallery: string[];
  stats: { label: string; value: string; icon: string }[];
  techStack: string[];
}> = {
  city: {
    id: 'city', title: '奥林匹斯城', subtitle: 'Olympus City · 2065', color: '#FF6B35', icon: 'ri-building-4-line',
    heroImg: 'https://public.readdy.ai/ai/img_res/1d4f44e39bcdc1eae7a1959c9780cf82.jpg',
    overview: '奥林匹斯城是人类在火星建造的第一座大型城市，位于太阳系最高山峰——奥林匹斯山脚下。城市由直径50公里的透明穹顶覆盖，内部通过旋转结构维持0.9G重力，完全模拟地球环境。\n\n城市采用"同心圆"规划结构：核心区是政府、医疗和教育设施；中环是商业和娱乐区；外环是居住区和垂直农业区。所有区域通过三层立体交通网络连接，出行时间不超过15分钟。\n\n奥林匹斯城的能源完全来自核聚变+太阳能双轨系统，实现零碳排放。水资源通过提取火星地下冰层获得，循环利用率达到99.9%。',
    techStack: ['穹顶结构工程', '人工重力系统', '大气循环系统', '核聚变能源', '垂直农业技术', '量子通信网络', '数字孪生城市管理', 'AI城市大脑'],
    specs: [
      { label: '穹顶直径', value: '50公里' },
      { label: '居住人口', value: '100万' },
      { label: '内部重力', value: '0.9G' },
      { label: '内部气压', value: '1标准大气压' },
      { label: '建设年份', value: '2065年' },
      { label: '能源自给率', value: '100%' },
    ],
    scenes: [
      {
        title: '立体交通网络', desc: 'eVTOL空中走廊、地面磁悬浮、地下超级高铁三层立体交通完美融合',
        detail: '奥林匹斯城的立体交通系统是人类工程史上最复杂的交通网络。空中层：城市上空划分为50个高度层，2000条eVTOL航线覆盖全城，AI统一调度，每秒处理100万架次飞行请求，事故率为零。地面层：磁悬浮轨道覆盖全城，速度300km/h，完全无噪音无排放。地下层：真空管道高铁连接城市各区，速度1200km/h，5分钟可到达城市任何角落。',
        icon: 'ri-flight-takeoff-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20city%20transportation%20eVTOL%20flying%20cars%20magnetic%20levitation%20underground%20tunnel%203D%20transport%20system%20orange%20neon%20glow%20dark%20cinematic%208k&width=600&height=400&seq=mars-city-sc1&orientation=landscape',
      },
      {
        title: '垂直农业森林', desc: '100层垂直农业大楼，年产粮食满足全城需求，同时提供氧气和调节气候',
        detail: '奥林匹斯城的垂直农业系统是城市生命支持的核心。100座100层高的垂直农业大楼分布在城市外环，每座大楼年产粮食10万吨，足够养活1万人。农业大楼同时承担氧气生产和气候调节功能，通过光合作用每天产生城市所需氧气的30%。AI精准农业系统实现了零农药、零化肥的完全有机生产，产量是传统农业的100倍。',
        icon: 'ri-plant-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20vertical%20farm%20agriculture%20tower%20green%20plants%20growing%20futuristic%20orange%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-city-sc2&orientation=landscape',
      },
      {
        title: '元宇宙生活中心', desc: '所有社交、娱乐、购物、办公都在元宇宙中完成，物理空间只是休息和体验的场所',
        detail: '奥林匹斯城的居民平均每天有12小时在元宇宙中度过。城市中心的"元宇宙广场"是全球最大的公共VR空间，可同时容纳100万人在线。居民可以在元宇宙中"回到"地球，与地球上的家人朋友相聚；可以探索宇宙任何角落；可以体验任何历史时期。元宇宙与现实的无缝切换，让火星居民的生活质量远超地球。',
        icon: 'ri-glasses-2-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20metaverse%20center%20VR%20virtual%20reality%20social%20hub%20futuristic%20orange%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-city-sc3&orientation=landscape',
      },
      {
        title: '量子能源系统', desc: '核聚变+太阳能双轨供能，城市能源完全自给自足，零碳排放',
        detail: '奥林匹斯城的能源系统由两部分组成：核聚变反应堆提供基础负荷，太阳能电池板提供峰值补充。火星轨道的太阳辐射强度是地球的43%，但城市穹顶覆盖面积巨大，太阳能发电量仍然可观。核聚变反应堆使用从火星地下提取的氘作为燃料，理论上可以运行数千年。城市能源完全自给自足，零碳排放，是人类历史上第一个真正意义上的零碳城市。',
        icon: 'ri-flashlight-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20fusion%20energy%20reactor%20solar%20power%20futuristic%20orange%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-city-sc4&orientation=landscape',
      },
    ],
    roadmap: [
      { year: '2035', event: '火星基地建设开始，首批100名宇航员抵达', status: 'future' },
      { year: '2040', event: '火星基地扩建，居住人口达到1000人', status: 'future' },
      { year: '2050', event: '奥林匹斯城穹顶开始建设，规模化移民启动', status: 'future' },
      { year: '2060', event: '穹顶完工，城市内部生态系统建立', status: 'future' },
      { year: '2065', event: '奥林匹斯城正式建成，居住人口达到100万', status: 'future' },
      { year: '2070', event: '火星城市网络形成，12座城市互联互通', status: 'future' },
    ],
    gallery: [
      'https://readdy.ai/api/search-image?query=Mars%20dome%20city%20exterior%20red%20planet%20surface%20orange%20neon%20dark%20background%208k&width=400&height=300&seq=mars-city-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Mars%20city%20interior%20green%20park%20people%20living%20orange%20neon%20dark%20background%208k&width=400&height=300&seq=mars-city-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Mars%20vertical%20farm%20agriculture%20tower%20orange%20neon%20dark%20background%208k&width=400&height=300&seq=mars-city-g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Mars%20city%20night%20view%20lights%20orange%20neon%20dark%20background%208k&width=400&height=300&seq=mars-city-g4&orientation=landscape',
    ],
    stats: [
      { label: '建设投资', value: '$50万亿', icon: 'ri-money-dollar-circle-line' },
      { label: '建设周期', value: '30年', icon: 'ri-time-line' },
      { label: '居住人口', value: '100万', icon: 'ri-group-line' },
      { label: '幸福指数', value: '9.8/10', icon: 'ri-heart-line' },
    ],
  },
  transport: {
    id: 'transport', title: '立体交通', subtitle: '3D Transport · Zero Accident', color: '#00FFCC', icon: 'ri-flight-takeoff-line',
    heroImg: 'https://readdy.ai/api/search-image?query=futuristic%20Mars%20city%20transportation%20eVTOL%20flying%20cars%20magnetic%20levitation%20underground%20tunnel%203D%20transport%20system%20cyan%20neon%20glow%20dark%20cinematic%208k%20ultra%20detailed%20wide%20panoramic&width=1400&height=600&seq=mars-transport-detail-hero&orientation=landscape',
    overview: '火星城市的立体交通系统是人类工程史上最复杂、最先进的交通网络。三层立体结构——空中eVTOL走廊、地面磁悬浮轨道、地下超级高铁——由AI统一调度，实现了零事故、零拥堵、零排放的完美交通体验。\n\n系统的核心是"量子交通大脑"——一个运行在量子计算机上的AI调度系统，每秒处理10亿次交通决策，实时优化全城交通流。系统可以预测未来30分钟的交通状况，提前调整路线，彻底消除拥堵。\n\n火星低重力（0.38G）为交通系统提供了独特优势：eVTOL的能耗只有地球的40%，磁悬浮的速度可以更高，地下高铁的建设成本更低。',
    techStack: ['量子交通大脑AI', 'eVTOL自动驾驶', '磁悬浮推进系统', '真空管道技术', '实时交通优化', '多模式无缝换乘', '能量回收系统', '6G实时通信'],
    specs: [
      { label: 'eVTOL航线数', value: '2000条' },
      { label: '地铁最高速度', value: '1200km/h' },
      { label: '日均出行次数', value: '500万次' },
      { label: '事故率', value: '0' },
      { label: '平均出行时间', value: '<15分钟' },
      { label: '能源消耗', value: '零碳' },
    ],
    scenes: [
      {
        title: 'eVTOL空中走廊', desc: '城市上空划分为100个高度层，AI统一调度，每秒处理100万架次飞行请求',
        detail: 'eVTOL空中走廊是奥林匹斯城最具视觉冲击力的交通系统。城市上空被划分为100个高度层，每层专用于特定类型的飞行器：最低层是短途通勤eVTOL，中间层是货运无人机，最高层是长途飞行器。量子AI调度系统每秒处理100万架次飞行请求，确保所有飞行器的安全间距。在火星0.38G的低重力环境下，eVTOL的能耗只有地球的40%，续航时间延长2.5倍。',
        icon: 'ri-flight-takeoff-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20eVTOL%20air%20corridor%20flying%20vehicles%20city%20sky%20cyan%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-transport-sc1&orientation=landscape',
      },
      {
        title: '磁悬浮地面网络', desc: '地面磁悬浮轨道覆盖全城，速度300km/h，完全无噪音无排放',
        detail: '地面磁悬浮网络是奥林匹斯城的主要公共交通系统，覆盖全城所有主要区域。列车采用超导磁悬浮技术，在火星低重力环境下速度可达300km/h，是地球磁悬浮的1.5倍。系统完全无噪音、无排放，能量回收效率达到95%。AI调度系统根据实时客流自动调整发车频率，高峰期每30秒一班，非高峰期每5分钟一班。',
        icon: 'ri-train-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20maglev%20train%20magnetic%20levitation%20city%20transport%20cyan%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-transport-sc2&orientation=landscape',
      },
      {
        title: '超级地下高铁', desc: '地下真空管道高铁，速度1200km/h，连接火星各大城市',
        detail: '地下超级高铁是连接火星各大城市的骨干交通系统。列车在真空管道中运行，消除了空气阻力，速度可达1200km/h。从奥林匹斯城到最近的城市只需30分钟，到最远的城市也只需3小时。管道建设利用了火星地下的熔岩管道，大幅降低了建设成本。系统采用磁悬浮推进，能量消耗极低，每人每公里能耗只有飞机的1/10。',
        icon: 'ri-subway-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20underground%20hyperloop%20vacuum%20tube%20train%20high%20speed%20cyan%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-transport-sc3&orientation=landscape',
      },
      {
        title: 'AI量子交通大脑', desc: '量子AI实时优化全城交通流，预测并消除拥堵，出行效率提升500%',
        detail: '量子交通大脑是整个交通系统的神经中枢，运行在奥林匹斯城的量子计算中心。系统实时处理来自全城100万个传感器的数据，每秒做出10亿次交通决策。AI可以预测未来30分钟的交通状况，提前调整路线和发车频率，彻底消除拥堵。系统还能在紧急情况下（如医疗急救、火灾）自动为紧急车辆开辟绿色通道，响应时间<1秒。',
        icon: 'ri-brain-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20AI%20traffic%20control%20quantum%20brain%20city%20management%20cyan%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-transport-sc4&orientation=landscape',
      },
    ],
    roadmap: [
      { year: '2040', event: '火星基地内部短途eVTOL系统建立', status: 'future' },
      { year: '2050', event: '城市内部磁悬浮网络建设开始', status: 'future' },
      { year: '2058', event: '地下超级高铁首条线路开通', status: 'future' },
      { year: '2063', event: '三层立体交通系统全面建成', status: 'future' },
      { year: '2065', event: '量子交通大脑上线，实现零事故运营', status: 'future' },
      { year: '2070', event: '城际超级高铁网络完成，12城互联', status: 'future' },
    ],
    gallery: [
      'https://readdy.ai/api/search-image?query=Mars%20eVTOL%20flying%20vehicles%20city%20sky%20cyan%20neon%20dark%20background%208k&width=400&height=300&seq=mars-transport-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Mars%20maglev%20train%20station%20futuristic%20cyan%20neon%20dark%20background%208k&width=400&height=300&seq=mars-transport-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Mars%20underground%20tunnel%20hyperloop%20cyan%20neon%20dark%20background%208k&width=400&height=300&seq=mars-transport-g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Mars%20traffic%20control%20center%20AI%20quantum%20cyan%20neon%20dark%20background%208k&width=400&height=300&seq=mars-transport-g4&orientation=landscape',
    ],
    stats: [
      { label: '日均出行', value: '500万次', icon: 'ri-user-line' },
      { label: '事故率', value: '0', icon: 'ri-shield-check-line' },
      { label: '平均出行时间', value: '<15分钟', icon: 'ri-time-line' },
      { label: '能源消耗', value: '零碳', icon: 'ri-leaf-line' },
    ],
  },
  life: {
    id: 'life', title: '火星生活', subtitle: 'Mars Life · Utopia 2065', color: '#A855F7', icon: 'ri-gamepad-line',
    heroImg: 'https://readdy.ai/api/search-image?query=Mars%20colony%20interior%20life%20people%20enjoying%20futuristic%20park%20green%20plants%20holographic%20entertainment%20happy%20lifestyle%20purple%20neon%20glow%20cinematic%208k%20ultra%20detailed%20wide%20panoramic&width=1400&height=600&seq=mars-life-detail-hero&orientation=landscape',
    overview: '在火星，人们享受着比地球更高质量的生活。这不是乌托邦幻想，而是技术进步的必然结果。AI接管了所有重复性工作，人类每周只需工作20小时，其余时间用于创造、探索和享乐。\n\n火星的医疗技术比地球领先20年——纳米机器人在体内巡逻，基因编辑消除了遗传疾病，平均寿命达到150岁。教育完全个性化，AI根据每个孩子的特点定制学习路径，学习效率是传统教育的10倍。\n\n最令人惊叹的是火星的夜空——两个月亮（火卫一和火卫二）同时升起，加上清晰可见的地球，构成了地球上永远看不到的壮观景象。',
    techStack: ['AI个性化教育', '纳米医疗机器人', '基因编辑技术', '元宇宙娱乐平台', '零重力运动设施', '意识数字化初代', '全感官VR系统', '量子心理治疗'],
    specs: [
      { label: '工作时长', value: '20小时/周' },
      { label: '平均寿命', value: '150岁' },
      { label: '幸福指数', value: '9.8/10' },
      { label: '犯罪率', value: '0.001%' },
      { label: '教育效率', value: '传统10倍' },
      { label: '医疗覆盖', value: '100%' },
    ],
    scenes: [
      {
        title: '元宇宙娱乐中心', desc: '全沉浸式元宇宙娱乐，可以体验地球任何历史时期，或探索宇宙任何角落',
        detail: '奥林匹斯城的元宇宙娱乐中心是全球最大的公共VR空间，可同时容纳100万人在线。居民可以在元宇宙中"回到"地球，与地球上的家人朋友相聚；可以探索宇宙任何角落；可以体验任何历史时期——从古罗马到未来2100年。系统还提供"意识旅行"服务，让用户以第一人称体验历史上任何人物的生活。',
        icon: 'ri-glasses-2-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20metaverse%20entertainment%20center%20VR%20virtual%20reality%20social%20hub%20purple%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-life-sc1&orientation=landscape',
      },
      {
        title: '零重力运动场', desc: '利用火星低重力区域，开展地球上不可能的极限运动，如真实飞行和超级跳跃',
        detail: '火星0.38G的低重力为运动提供了全新可能。零重力运动场是一个直径500米的巨型球形空间，内部重力可以调节从0G到1G。在0.1G环境下，人类可以真正飞翔——用双臂扑打就能在空中滑翔数百米。超级跳跃运动让运动员可以跳到20米高。这些在地球上只能在梦中体验的运动，在火星成为了日常娱乐。',
        icon: 'ri-run-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20low%20gravity%20sports%20arena%20zero%20gravity%20flying%20jumping%20purple%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-life-sc2&orientation=landscape',
      },
      {
        title: '星际旅游体验', desc: '定期开展地球-月球-火星旅游线路，以及木星、土星环游览项目',
        detail: '奥林匹斯城是太阳系旅游的中转站。每月有定期航班往返地球，旅行时间6个月；每季度有月球快线，旅行时间3天。更令人兴奋的是木星旅游——乘坐核聚变飞船，2年可以到达木星，近距离观察大红斑，在木卫二的地下海洋中潜水。土星环游览是最受欢迎的项目，在土星环中穿行的体验被描述为"宇宙中最美的风景"。',
        icon: 'ri-rocket-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20space%20tourism%20interplanetary%20travel%20Jupiter%20Saturn%20rings%20purple%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-life-sc3&orientation=landscape',
      },
      {
        title: '数字永生服务', desc: '意识数字化技术让人类可以在虚拟世界中永生，与后代永远相伴',
        detail: '数字永生是火星最具争议也最令人向往的服务。通过意识数字化技术，人类的记忆、个性和思维模式可以被完整地数字化存储。数字意识可以在元宇宙中继续生活，与后代互动，甚至继续创作和工作。目前已有超过10万名火星居民选择了数字永生服务，他们的数字意识在元宇宙中继续活跃，成为连接过去与未来的桥梁。',
        icon: 'ri-heart-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20digital%20immortality%20consciousness%20upload%20virtual%20afterlife%20purple%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-life-sc4&orientation=landscape',
      },
    ],
    roadmap: [
      { year: '2040', event: '火星基地建立基础生活设施，100人居住', status: 'future' },
      { year: '2050', event: '火星城市初步建成，基本生活服务完善', status: 'future' },
      { year: '2058', event: '元宇宙娱乐中心建成，零重力运动场开放', status: 'future' },
      { year: '2063', event: '医疗技术达到地球最高水平，平均寿命120岁', status: 'future' },
      { year: '2065', event: '数字永生服务上线，意识数字化技术成熟', status: 'future' },
      { year: '2070', event: '平均寿命达到150岁，幸福指数超越地球', status: 'future' },
    ],
    gallery: [
      'https://readdy.ai/api/search-image?query=Mars%20city%20park%20green%20plants%20people%20happy%20purple%20neon%20dark%20background%208k&width=400&height=300&seq=mars-life-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Mars%20zero%20gravity%20sports%20arena%20flying%20purple%20neon%20dark%20background%208k&width=400&height=300&seq=mars-life-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Mars%20metaverse%20entertainment%20VR%20center%20purple%20neon%20dark%20background%208k&width=400&height=300&seq=mars-life-g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Mars%20night%20sky%20two%20moons%20earth%20visible%20purple%20neon%20dark%20background%208k&width=400&height=300&seq=mars-life-g4&orientation=landscape',
    ],
    stats: [
      { label: '平均寿命', value: '150岁', icon: 'ri-heart-pulse-line' },
      { label: '工作时长', value: '20小时/周', icon: 'ri-time-line' },
      { label: '幸福指数', value: '9.8/10', icon: 'ri-emotion-happy-line' },
      { label: '犯罪率', value: '0.001%', icon: 'ri-shield-check-line' },
    ],
  },
  starlink: {
    id: 'starlink', title: '火星星链', subtitle: 'Mars Starlink · Solar Internet', color: '#FFD700', icon: 'ri-global-line',
    heroImg: 'https://readdy.ai/api/search-image?query=Mars%20satellite%20network%20starlink%20constellation%20orbit%20red%20planet%20space%20communication%20network%20gold%20yellow%20glowing%20cinematic%208k%20ultra%20detailed%20wide%20panoramic&width=1400&height=600&seq=mars-starlink-detail-hero&orientation=landscape',
    overview: '火星星链是围绕火星部署的360颗通信卫星网络，实现火星全球覆盖，并与地球星链网络连接，构建太阳系互联网基础设施。这是人类历史上第一个跨行星的互联网系统。\n\n火星星链的核心挑战是地火通信延迟——由于光速限制，地球与火星之间的通信延迟在3-22分钟之间（取决于两星球的相对位置）。为了解决这个问题，META66与量子通信研究院合作，开发了"量子纠缠中继"技术，实现了特定类型数据的即时传输。\n\n火星星链不仅服务于火星城市，还为太阳系内的所有飞船提供导航和通信服务，是人类走向星际文明的关键基础设施。',
    techStack: ['低轨卫星星座', '量子纠缠通信', '激光星间链路', '深空导航系统', '太阳能卫星供电', '抗辐射电子系统', '自主轨道维护', '星际互联网协议'],
    specs: [
      { label: '卫星数量', value: '360颗' },
      { label: '轨道高度', value: '500km' },
      { label: '通信延迟', value: '3-22分钟' },
      { label: '带宽', value: '100Gbps' },
      { label: '覆盖率', value: '火星100%' },
      { label: '量子通信', value: '即时加密' },
    ],
    scenes: [
      {
        title: '火星全球覆盖', desc: '360颗卫星确保火星任何位置都有稳定的高速网络连接',
        detail: '360颗卫星分布在三个轨道面，确保火星任何位置在任何时刻都有至少3颗卫星可见。每颗卫星配备激光星间链路，卫星之间可以直接通信，无需经过地面站中转。系统提供100Gbps的下行带宽，足以支持火星城市的所有通信需求，包括高清视频、元宇宙数据流和科学数据传输。',
        icon: 'ri-global-line',
        img: 'https://readdy.ai/api/search-image?query=Mars%20satellite%20coverage%20network%20constellation%20orbit%20gold%20yellow%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-starlink-sc1&orientation=landscape',
      },
      {
        title: '地火量子通信', desc: '利用量子纠缠技术，实现地球与火星之间的即时量子加密通信',
        detail: '量子纠缠通信是火星星链最革命性的技术。通过在地球和火星之间建立量子纠缠对，可以实现特定类型信息的即时传输，突破光速限制。目前量子通信主要用于加密密钥分发和紧急指令传输，带宽有限但安全性绝对可靠。随着量子中继技术的成熟，量子通信带宽将逐步提升，最终实现地火之间的即时高带宽通信。',
        icon: 'ri-cpu-line',
        img: 'https://readdy.ai/api/search-image?query=quantum%20communication%20Earth%20Mars%20entanglement%20instant%20transmission%20gold%20yellow%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-starlink-sc2&orientation=landscape',
      },
      {
        title: '星际导航系统', desc: '为太阳系内所有飞船提供精确导航，精度达到厘米级',
        detail: '火星星链的导航系统是太阳系内最精确的定位系统。通过与地球GPS、月球导航系统和火星星链的三角定位，可以在太阳系内任何位置实现厘米级精度的定位。系统为所有太阳系内的飞船提供实时导航服务，包括货运飞船、旅游飞船和科学探测器。在小行星带等复杂环境中，系统还提供障碍物预警和自动避障功能。',
        icon: 'ri-map-pin-line',
        img: 'https://readdy.ai/api/search-image?query=solar%20system%20navigation%20GPS%20satellite%20positioning%20spacecraft%20gold%20yellow%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-starlink-sc3&orientation=landscape',
      },
      {
        title: '太阳系互联网', desc: '连接地球、月球、火星、木星基地，构建人类第一个星际互联网',
        detail: '太阳系互联网是人类文明史上最宏大的基础设施项目。它连接了地球、月球、火星、木星基地和小行星带矿业站，构建了一个覆盖整个太阳系的通信网络。虽然受光速限制，不同节点之间存在分钟到小时级的延迟，但系统采用"延迟容忍网络"（DTN）协议，确保数据最终可靠传输。太阳系互联网是人类走向星际文明的第一步。',
        icon: 'ri-links-line',
        img: 'https://readdy.ai/api/search-image?query=solar%20system%20internet%20network%20Earth%20Moon%20Mars%20Jupiter%20connected%20gold%20yellow%20neon%20dark%20background%20cinematic%208k&width=600&height=400&seq=mars-starlink-sc4&orientation=landscape',
      },
    ],
    roadmap: [
      { year: '2030', event: '火星通信卫星首批10颗发射，基础通信建立', status: 'future' },
      { year: '2040', event: '火星星链一期完成，100颗卫星，覆盖率80%', status: 'future' },
      { year: '2050', event: '火星星链二期完成，360颗卫星，全球覆盖', status: 'future' },
      { year: '2055', event: '量子通信中继站建立，地火即时加密通信实现', status: 'future' },
      { year: '2060', event: '太阳系互联网协议标准化，多星球互联', status: 'future' },
      { year: '2070', event: '太阳系互联网全面建成，覆盖所有人类基地', status: 'future' },
    ],
    gallery: [
      'https://readdy.ai/api/search-image?query=Mars%20satellite%20constellation%20orbit%20space%20gold%20yellow%20neon%20dark%20background%208k&width=400&height=300&seq=mars-starlink-g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=quantum%20communication%20laser%20beam%20space%20gold%20yellow%20neon%20dark%20background%208k&width=400&height=300&seq=mars-starlink-g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=solar%20system%20network%20map%20planets%20connected%20gold%20yellow%20neon%20dark%20background%208k&width=400&height=300&seq=mars-starlink-g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=Mars%20ground%20station%20antenna%20communication%20gold%20yellow%20neon%20dark%20background%208k&width=400&height=300&seq=mars-starlink-g4&orientation=landscape',
    ],
    stats: [
      { label: '卫星数量', value: '360颗', icon: 'ri-global-line' },
      { label: '覆盖率', value: '100%', icon: 'ri-signal-tower-line' },
      { label: '带宽', value: '100Gbps', icon: 'ri-speed-line' },
      { label: '服务飞船', value: '1000+', icon: 'ri-rocket-line' },
    ],
  },
};

export default function MarsDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'scenes' | 'roadmap' | 'gallery'>('overview');
  const [selectedScene, setSelectedScene] = useState<number | null>(null);
  const [animIn, setAnimIn] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const detail = id ? MARS_DETAILS[id] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setAnimIn(true), 100);
    if (detail) {
      document.title = `${detail.title} | META66 火星城市深度解析 · ${detail.subtitle}`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', `META66火星城市 ${detail.title}深度解析：${detail.overview.slice(0, 120)}...`);
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) metaKeywords.setAttribute('content', `${detail.title}, 火星城市, META66, 星际移民, ${detail.techStack.slice(0, 3).join(', ')}`);
    }
  }, [id, detail]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const stars: { x: number; y: number; r: number; alpha: number; speed: number }[] = [];
    for (let i = 0; i < 150; i++) {
      stars.push({ x: Math.random() * canvas.width, y: Math.random() * canvas.height, r: Math.random() * 1.5 + 0.3, alpha: Math.random() * 0.5 + 0.1, speed: Math.random() * 0.02 + 0.005 });
    }
    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.alpha += s.speed;
        if (s.alpha > 0.7 || s.alpha < 0.1) s.speed = -s.speed;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${s.alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animId);
  }, []);

  if (!detail) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#020810' }}>
        <div className="text-center">
          <p className="text-white/50 text-xl mb-4">页面不存在</p>
          <button onClick={() => navigate('/mars')} className="px-6 py-3 rounded-xl font-bold text-black cursor-pointer" style={{ background: '#FF6B35' }}>返回火星页面</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: '深度概述', icon: 'ri-information-line' },
    { id: 'scenes', label: '核心场景', icon: 'ri-apps-line' },
    { id: 'roadmap', label: '建设路线', icon: 'ri-map-pin-time-line' },
    { id: 'gallery', label: '图片展示', icon: 'ri-image-line' },
  ] as const;

  return (
    <div className="min-h-screen overflow-hidden" style={{ background: 'linear-gradient(180deg, #020810 0%, #0A0408 50%, #020810 100%)', fontFamily: "'Inter', 'PingFang SC', sans-serif" }}>
      <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />

      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 opacity-4" style={{ background: 'radial-gradient(circle at 20% 50%, rgba(255,107,53,0.15) 0%, transparent 40%)' }} />
      </div>

      {/* Header */}
      <div className="sticky top-0 z-50" style={{ background: 'rgba(2,8,16,0.95)', borderBottom: `1px solid ${detail.color}20`, backdropFilter: 'blur(20px)' }}>
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/mars')} className="flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap hover:scale-105" style={{ color: 'rgba(255,255,255,0.5)' }}>
              <i className="ri-arrow-left-line" />
              <span className="text-sm">返回火星</span>
            </button>
            <span className="text-white/20">/</span>
            <span className="text-sm font-bold" style={{ color: detail.color }}>{detail.title}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center" style={{ background: `${detail.color}20`, border: `1.5px solid ${detail.color}50`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
              <i className={`${detail.icon} text-white text-sm`} />
            </div>
            <span className="font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.9rem' }}>MARS · {detail.title.toUpperCase()}</span>
          </div>
          <div className="text-sm" style={{ color: `${detail.color}70`, fontFamily: 'Caveat, cursive' }}>2065 · 深度解析</div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative z-10 h-[480px] overflow-hidden">
        <img src={detail.heroImg} alt={detail.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(2,8,16,0.2) 0%, rgba(2,8,16,0.5) 50%, rgba(2,8,16,1) 100%)' }} />
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 30% 50%, ${detail.color}10 0%, transparent 60%)` }} />

        <div className="absolute inset-0 flex items-center px-8 md:px-16">
          <div style={{ opacity: animIn ? 1 : 0, transform: animIn ? 'translateX(0)' : 'translateX(-40px)', transition: 'all 0.9s ease' }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-5" style={{ background: `${detail.color}15`, border: `1.5px solid ${detail.color}40`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%' }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: detail.color }} />
              <span className="text-sm font-bold" style={{ color: detail.color, fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>MARS DEEP DIVE · {detail.subtitle}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-4 leading-tight" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(135deg, ${detail.color}, #FFD700)` }}>MARS</span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-black text-white mb-4">{detail.title}</h2>
            <div className="flex flex-wrap gap-4 mt-6">
              {detail.stats.map(s => (
                <div key={s.label} className="px-4 py-2" style={{ background: 'rgba(2,8,16,0.7)', border: `1px solid ${detail.color}30`, borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px', backdropFilter: 'blur(10px)' }}>
                  <p className="font-black text-lg" style={{ color: detail.color, fontFamily: 'Orbitron, sans-serif' }}>{s.value}</p>
                  <p className="text-white/40 text-xs">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky z-40" style={{ top: '65px', background: 'rgba(2,8,16,0.95)', borderBottom: `1px solid ${detail.color}15`, backdropFilter: 'blur(20px)' }}>
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex gap-1 py-3">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap cursor-pointer" style={{ background: activeTab === tab.id ? `${detail.color}15` : 'transparent', color: activeTab === tab.id ? detail.color : 'rgba(255,255,255,0.4)', border: `1px solid ${activeTab === tab.id ? detail.color + '40' : 'transparent'}` }}>
                <i className={tab.icon} />{tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-12">

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 flex flex-col gap-6">
              <div className="p-8 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${detail.color}15`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <h3 className="text-white font-black text-xl mb-5 flex items-center gap-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  <i className="ri-information-line" style={{ color: detail.color }} />深度概述
                </h3>
                {detail.overview.split('\n\n').map((para, i) => (
                  <p key={i} className="text-white/60 leading-relaxed text-base mb-4 last:mb-0">{para}</p>
                ))}
              </div>
              <div className="p-8 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${detail.color}15`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <h3 className="text-white font-black text-xl mb-5 flex items-center gap-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  <i className="ri-stack-line" style={{ color: detail.color }} />核心技术
                </h3>
                <div className="flex flex-wrap gap-3">
                  {detail.techStack.map(tech => (
                    <span key={tech} className="px-4 py-2 text-sm font-semibold" style={{ background: `${detail.color}10`, color: detail.color, border: `1px solid ${detail.color}25`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', fontFamily: 'Caveat, cursive', fontSize: '0.9rem' }}>{tech}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="p-6 rounded-2xl" style={{ background: `${detail.color}06`, border: `1.5px solid ${detail.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <h3 className="text-white font-black text-lg mb-5" style={{ fontFamily: 'Orbitron, sans-serif' }}>关键数据</h3>
                {detail.specs.map(spec => (
                  <div key={spec.label} className="flex justify-between items-center py-3 border-b" style={{ borderColor: `${detail.color}10` }}>
                    <span className="text-white/50 text-sm">{spec.label}</span>
                    <span className="font-black text-base" style={{ color: detail.color, fontFamily: 'Orbitron, sans-serif' }}>{spec.value}</span>
                  </div>
                ))}
              </div>
              <div className="p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px' }}>
                <h3 className="text-white font-black text-lg mb-4">探索其他板块</h3>
                {Object.values(MARS_DETAILS).filter(d => d.id !== detail.id).map(d => (
                  <button key={d.id} onClick={() => navigate(`/mars/${d.id}`)} className="w-full flex items-center gap-3 p-3 rounded-xl mb-2 last:mb-0 cursor-pointer transition-all hover:-translate-y-0.5" style={{ background: `${d.color}06`, border: `1px solid ${d.color}15` }}>
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ background: `${d.color}15`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                      <i className={`${d.icon} text-sm`} style={{ color: d.color }} />
                    </div>
                    <span className="text-white/60 text-sm font-medium">{d.title}</span>
                    <i className="ri-arrow-right-line ml-auto text-xs" style={{ color: d.color }} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'scenes' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {detail.scenes.map((scene, i) => (
              <div key={scene.title} className="group rounded-2xl overflow-hidden cursor-pointer transition-all duration-400 hover:-translate-y-2" style={{ background: 'rgba(255,255,255,0.02)', border: `1.5px solid ${detail.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px', opacity: animIn ? 1 : 0, transform: animIn ? 'translateY(0)' : 'translateY(30px)', transition: `all 0.6s ease ${i * 100}ms` }} onClick={() => setSelectedScene(selectedScene === i ? null : i)}>
                <div className="relative h-52 overflow-hidden">
                  <img src={scene.img} alt={scene.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 30%, rgba(2,8,16,0.95) 100%)' }} />
                  <div className="absolute top-3 left-3 w-10 h-10 flex items-center justify-center" style={{ background: `${detail.color}20`, border: `1.5px solid ${detail.color}40`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%' }}>
                    <i className={`${scene.icon} text-lg`} style={{ color: detail.color }} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-white font-black text-lg mb-2">{scene.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed mb-4">{scene.desc}</p>
                  {selectedScene === i && (
                    <div className="p-4 rounded-xl mb-4" style={{ background: `${detail.color}08`, border: `1px solid ${detail.color}20` }}>
                      <p className="text-white/65 text-sm leading-relaxed">{scene.detail}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm font-bold" style={{ color: detail.color }}>
                    <span>{selectedScene === i ? '收起详情' : '查看深度解析'}</span>
                    <i className={selectedScene === i ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'roadmap' && (
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <div className="absolute left-8 top-0 bottom-0 w-px" style={{ background: `linear-gradient(to bottom, ${detail.color}60, transparent)` }} />
              {detail.roadmap.map((item, i) => (
                <div key={item.year} className="flex items-start gap-6 mb-8 last:mb-0" style={{ opacity: animIn ? 1 : 0, transform: animIn ? 'translateX(0)' : 'translateX(-30px)', transition: `all 0.6s ease ${i * 100}ms` }}>
                  <div className="w-16 h-16 flex items-center justify-center flex-shrink-0 z-10 font-black text-sm" style={{ background: item.status === 'done' ? detail.color : item.status === 'active' ? `${detail.color}30` : 'rgba(255,255,255,0.05)', border: `2px solid ${item.status === 'future' ? 'rgba(255,255,255,0.1)' : detail.color}`, borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%', color: item.status === 'done' ? '#000' : item.status === 'active' ? detail.color : 'rgba(255,255,255,0.3)', fontFamily: 'Orbitron, sans-serif', fontSize: '0.7rem' }}>
                    {item.status === 'done' ? <i className="ri-check-line text-xl" /> : item.year.slice(2)}
                  </div>
                  <div className="flex-1 pt-3 pb-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-black text-xl" style={{ color: item.status === 'future' ? 'rgba(255,255,255,0.3)' : detail.color, fontFamily: 'Orbitron, sans-serif' }}>{item.year}</span>
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full" style={{ background: item.status === 'done' ? `${detail.color}20` : item.status === 'active' ? `${detail.color}15` : 'rgba(255,255,255,0.05)', color: item.status === 'done' ? detail.color : item.status === 'active' ? detail.color : 'rgba(255,255,255,0.2)', border: `1px solid ${item.status === 'future' ? 'rgba(255,255,255,0.08)' : detail.color + '30'}` }}>
                        {item.status === 'done' ? '已完成' : item.status === 'active' ? '进行中' : '规划中'}
                      </span>
                    </div>
                    <p className="text-white/60 text-base leading-relaxed">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'gallery' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {detail.gallery.map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden group cursor-pointer" style={{ height: '280px', border: `1.5px solid ${detail.color}20`, borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px', opacity: animIn ? 1 : 0, transform: animIn ? 'translateY(0)' : 'translateY(30px)', transition: `all 0.6s ease ${i * 100}ms` }}>
                <img src={img} alt={`${detail.title} ${i + 1}`} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `${detail.color}15` }} />
                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="px-3 py-1.5 text-xs font-bold" style={{ background: `${detail.color}20`, color: detail.color, border: `1px solid ${detail.color}40`, borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%' }}>{detail.title} · 图{i + 1}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
